import { Pagination, useMediaQuery } from "@mui/material";
import { AxiosError } from "axios";
import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
    ArrowDownIcon,
    DownloadIcon,
    GraduationCapIcon,
    InfoIcon,
} from "../../../assets/img/icons";
import Button from "../../../components/Button";
import IconButton from "../../../components/IconButton";
import OpenInNewTabLink from "../../../components/OpenInNewTabLink";
import MemberDetails, {
    MemberDetailsUser,
} from "../../Members/MemberDetails";
import ConfirmModal from "../../../components/ConfirmModal";
import ConnectedLayout from "../../../components/ConnectedLayout";
import Header from "../../../components/Header";
import Input from "../../../components/Input";
import { Select } from "../../../components/OldSelect";
import Table from "../../../components/Table";
import { AuthContext } from "../../../contexts/AuthContext";
import {
    cancelAdminGraduationValidation,
    getAdminGraduation,
    getAdminGraduationExport,
    promoteEligibleAdminGraduation,
    validateAdminGraduation,
} from "../../../services/api/admin/graduation";
import {
    getPromotionOptions,
    Promotion,
} from "../../../services/api/admin/promotions";
import {
    GraduationMainBlocker,
    GraduationMainBlockerFilter,
    GraduationSortBy,
    GraduationSortOrder,
    GraduationStatusLabel,
    GraduationUserRow,
    GraduationUserStatus,
} from "../../../types/api/admin/graduation";
import { Role } from "../../../types/enums/Role";
import { getFormattedFrDate } from "../../../utils/date";
import {
    canShowDemoteAction,
    canShowPromoteAction,
    GRADUATION_VALIDATION_MIN_PROGRESS,
    getDemoteButtonLabel,
    getNextTraderRole,
    getPreviousTraderRole,
    getPromoteButtonLabel,
} from "../../../utils/graduation";
import { getUserName } from "../../../utils/string";
import styles from "./styles.module.scss";

const blockerLabelMap: Record<GraduationMainBlocker, string> = {
    none: "Aucun",
    sessions: "Sessions",
    day_win: "Day win",
    funding: "Financement",
    method: "Méthode",
};

const ROLE_FILTER_OPTIONS = [
    Role.Junior,
    Role.Expert,
    Role.Pro,
    Role.Independant,
    Role.Forme,
] as const;

const isConcretePromotionId = (value: string) =>
    value !== "" && value !== "none";

const GraduationAdmin: FC = () => {
    const navigate = useNavigate();
    const isMobile = useMediaQuery("(max-width: 1050px)");
    const { hasPermission, isStreamOn } = useContext(AuthContext)!;
    const canValidateAdminGraduation = hasPermission(
        "admin-graduation",
        "validate"
    );
    const [loading, setLoading] = useState(false);
    const [actionLoadingUserId, setActionLoadingUserId] = useState<string | null>(
        null
    );
    const [cancelValidationUserId, setCancelValidationUserId] = useState<
        string | null
    >(null);
    const [bulkPromoteConfirmOpen, setBulkPromoteConfirmOpen] = useState(false);
    const [notesUser, setNotesUser] = useState<MemberDetailsUser | null>(null);
    const [bulkPromoteLoading, setBulkPromoteLoading] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [users, setUsers] = useState<GraduationUserRow[]>([]);
    const [page, setPage] = useState(1);
    const [maxPage, setMaxPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [searchInput, setSearchInput] = useState("");
    const [debouncedSearchInput, setDebouncedSearchInput] = useState("");
    const [status, setStatus] = useState<GraduationUserStatus | "">("");
    const [roleFilter, setRoleFilter] = useState<string>("");
    const [promotionFilter, setPromotionFilter] = useState<string>("");
    const [promoOptions, setPromoOptions] = useState<Promotion[]>([]);
    const [mainBlocker, setMainBlocker] = useState<
        GraduationMainBlockerFilter | ""
    >(
        ""
    );
    const [minScoreInput, setMinScoreInput] = useState("");
    const [maxScoreInput, setMaxScoreInput] = useState("");
    const [sortBy, setSortBy] = useState<GraduationSortBy>("score");
    const [scoreSortOrder, setScoreSortOrder] =
        useState<GraduationSortOrder>("desc");
    const [filtersReady, setFiltersReady] = useState(false);
    const [summary, setSummary] = useState({
        total: 0,
        trained: 0,
        eligible: 0,
        in_progress: 0,
    });
    const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const latestRequestId = useRef(0);
    const hasActiveFilters =
        searchInput.trim() !== "" ||
        status !== "" ||
        roleFilter !== "" ||
        promotionFilter !== "" ||
        mainBlocker !== "" ||
        minScoreInput !== "" ||
        maxScoreInput !== "";

    useEffect(() => {
        getPromotionOptions()
            .then((rows) => setPromoOptions(rows ?? []))
            .catch(() => {
                /* filtre promo optionnel */
            });
    }, []);

    useEffect(() => {
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => {
            setDebouncedSearchInput(searchInput.trim());
        }, 400);
        return () => {
            if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        };
    }, [searchInput]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        const qSearch = params.get("search");
        if (qSearch !== null) {
            setSearchInput(qSearch);
            setDebouncedSearchInput(qSearch.trim());
        }

        const qStatus = params.get("status");
        if (
            qStatus === "trained" ||
            qStatus === "eligible" ||
            qStatus === "in_progress"
        ) {
            setStatus(qStatus);
        }

        const qRole = params.get("role");
        if (qRole && ROLE_FILTER_OPTIONS.includes(qRole as (typeof ROLE_FILTER_OPTIONS)[number])) {
            setRoleFilter(qRole);
        }

        const qPromotionId = params.get("promotionId");
        if (qPromotionId) {
            setPromotionFilter(qPromotionId);
        }

        const qMainBlocker = params.get("mainBlocker");
        if (
            qMainBlocker === "none" ||
            qMainBlocker === "sessions" ||
            qMainBlocker === "day_win" ||
            qMainBlocker === "funding"
        ) {
            setMainBlocker(qMainBlocker);
        }

        const qSortBy = params.get("sortBy");
        if (qSortBy === "score" || qSortBy === "role" || qSortBy === "status") {
            setSortBy(qSortBy);
        }

        const qSortOrder = params.get("sortOrder");
        if (qSortOrder === "asc" || qSortOrder === "desc") {
            setScoreSortOrder(qSortOrder);
        }

        const qMinScore = params.get("minScore");
        if (qMinScore && !isNaN(Number(qMinScore))) {
            const minScore = Math.min(100, Math.max(0, Number(qMinScore)));
            setMinScoreInput(String(minScore));
        }

        const qMaxScore = params.get("maxScore");
        if (qMaxScore && !isNaN(Number(qMaxScore))) {
            const maxScore = Math.min(100, Math.max(0, Number(qMaxScore)));
            setMaxScoreInput(String(maxScore));
        }

        const qLimit = params.get("limit");
        if (qLimit && !isNaN(Number(qLimit))) {
            const nextLimit = Math.min(100, Math.max(1, Number(qLimit)));
            setLimit(nextLimit);
        }

        const qPage = params.get("page");
        if (qPage && !isNaN(Number(qPage))) {
            setPage(Math.max(1, Number(qPage)));
        }

        setFiltersReady(true);
    }, []);

    useEffect(() => {
        if (!filtersReady) return;

        const params = new URLSearchParams();
        if (debouncedSearchInput) params.set("search", debouncedSearchInput);
        if (status) params.set("status", status);
        if (roleFilter) params.set("role", roleFilter);
        if (promotionFilter) params.set("promotionId", promotionFilter);
        if (mainBlocker) params.set("mainBlocker", mainBlocker);
        if (minScoreInput !== "") params.set("minScore", minScoreInput);
        if (maxScoreInput !== "") params.set("maxScore", maxScoreInput);
        params.set("sortBy", sortBy);
        params.set("sortOrder", scoreSortOrder);
        params.set("page", String(page));
        params.set("limit", String(limit));

        const newRelativePathQuery = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState(null, "", newRelativePathQuery);
    }, [
        filtersReady,
        debouncedSearchInput,
        status,
        roleFilter,
        promotionFilter,
        mainBlocker,
        minScoreInput,
        maxScoreInput,
        sortBy,
        scoreSortOrder,
        page,
        limit,
    ]);

    const minScore =
        minScoreInput === ""
            ? undefined
            : Math.min(100, Math.max(0, Number(minScoreInput)));
    const maxScore =
        maxScoreInput === ""
            ? undefined
            : Math.min(100, Math.max(0, Number(maxScoreInput)));
    const normalizedMinScore =
        minScore !== undefined && maxScore !== undefined
            ? Math.min(minScore, maxScore)
            : minScore;
    const normalizedMaxScore =
        minScore !== undefined && maxScore !== undefined
            ? Math.max(minScore, maxScore)
            : maxScore;
    const handleResetFilters = () => {
        setSearchInput("");
        setDebouncedSearchInput("");
        setStatus("");
        setRoleFilter("");
        setPromotionFilter("");
        setMainBlocker("");
        setMinScoreInput("");
        setMaxScoreInput("");
        setSortBy("score");
        setScoreSortOrder("desc");
        setLimit(20);
        setPage(1);
    };

    const handleExportGraduationCSV = async () => {
        try {
            const data = await getAdminGraduationExport({
                search: debouncedSearchInput || undefined,
                role: roleFilter || undefined,
                promotionId: promotionFilter || undefined,
                status: status || undefined,
                mainBlocker: mainBlocker || undefined,
                minScore: normalizedMinScore,
                maxScore: normalizedMaxScore,
                sortBy,
                sortOrder: scoreSortOrder,
            });

            const blob = new Blob([data ?? ""], {
                type: "text/csv;charset=utf-8;",
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `apprentissage_${new Date().toISOString().split("T")[0]}.csv`
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            if (error instanceof AxiosError && error.response?.status === 403) {
                toast.error("Vous n'avez pas les droits pour exporter");
                return;
            }
            toast.error("Erreur lors de l'export apprentissage");
        }
    };

    const selectedPromotionName =
        promoOptions.find((promo) => promo.id === promotionFilter)?.name ??
        "cette promotion";
    const canBulkPromoteEligible =
        canValidateAdminGraduation && isConcretePromotionId(promotionFilter);

    const handleConfirmBulkPromoteEligible = async () => {
        if (!isConcretePromotionId(promotionFilter)) return;
        setBulkPromoteLoading(true);
        try {
            const result = await promoteEligibleAdminGraduation(promotionFilter);
            const promotedCount = result?.promotedCount ?? 0;
            const failedCount = result?.failedCount ?? 0;
            if (failedCount > 0) {
                toast.warning(
                    `${promotedCount} promotion(s) effectuée(s), ${failedCount} échec(s).`
                );
            } else if (promotedCount === 0) {
                toast.info(
                    `Aucun membre à ${GRADUATION_VALIDATION_MIN_PROGRESS} % ou plus à promouvoir dans cette promo.`
                );
            } else {
                toast.success(
                    `${promotedCount} membre(s) à ${GRADUATION_VALIDATION_MIN_PROGRESS} % ou plus promu(s) (${selectedPromotionName}).`
                );
            }
            setBulkPromoteConfirmOpen(false);
            setRefreshKey((prev) => prev + 1);
        } catch (error) {
            if (error instanceof AxiosError && error.response?.status === 403) {
                toast.error("Vous n'avez pas les droits pour promouvoir");
                return;
            }
            toast.error("Échec de la promotion groupée des éligibles.");
        } finally {
            setBulkPromoteLoading(false);
        }
    };

    const handleConfirmCancelValidation = async () => {
        if (!cancelValidationUserId) return;
        const targetRole = users.find(
            (user) => user.userId === cancelValidationUserId
        )?.role;
        setActionLoadingUserId(cancelValidationUserId);
        try {
            await cancelAdminGraduationValidation(cancelValidationUserId);
            toast.success(
                targetRole === Role.Forme
                    ? "Validation admin annulée."
                    : "Rétrogradation effectuée."
            );
            setRefreshKey((prev) => prev + 1);
            setCancelValidationUserId(null);
        } catch {
            toast.error(
                targetRole === Role.Forme
                    ? "Échec de l'annulation de validation."
                    : "Échec de la rétrogradation."
            );
        } finally {
            setActionLoadingUserId(null);
        }
    };

    useEffect(() => {
        if (!filtersReady) return;
        const requestId = ++latestRequestId.current;
        setLoading(true);
        getAdminGraduation({
            page,
            limit,
            search: debouncedSearchInput || undefined,
            role: roleFilter || undefined,
            promotionId: promotionFilter || undefined,
            status: status || undefined,
            mainBlocker: mainBlocker || undefined,
            minScore: normalizedMinScore,
            maxScore: normalizedMaxScore,
            sortBy,
            sortOrder: scoreSortOrder,
        })
            .then((response) => {
                if (requestId !== latestRequestId.current) return;
                if (!response) {
                    setUsers([]);
                    setSummary({ total: 0, trained: 0, eligible: 0, in_progress: 0 });
                    setMaxPage(1);
                    return;
                }

                setUsers(response.users ?? []);
                setSummary(
                    response.summary ?? {
                        total: 0,
                        trained: 0,
                        eligible: 0,
                        in_progress: 0,
                    }
                );
                setMaxPage(
                    Math.max(
                        1,
                        response.pagination?.totalPages ??
                            response.pagination?.total_pages ??
                            1
                    )
                );
            })
            .catch(() => {
                if (requestId !== latestRequestId.current) return;
                setUsers([]);
                setSummary({ total: 0, trained: 0, eligible: 0, in_progress: 0 });
                setMaxPage(1);
            })
            .finally(() => {
                if (requestId === latestRequestId.current) {
                    setLoading(false);
                }
            });
    }, [
        filtersReady,
        page,
        limit,
        debouncedSearchInput,
        roleFilter,
        promotionFilter,
        status,
        mainBlocker,
        normalizedMinScore,
        normalizedMaxScore,
        sortBy,
        scoreSortOrder,
        refreshKey,
    ]);

    const rows = useMemo(
        () => [
            {
                label: "NOM",
                value: "member",
                className: styles.nameKey,
                width: "140px",
            },
            {
                label: (
                    <div className={styles.scoreHeaderContent}>
                        RÔLE
                        <span className={styles.scoreSortIcon}>
                            <ArrowDownIcon
                                width={12}
                                style={{
                                    transform:
                                        sortBy === "role" &&
                                        scoreSortOrder === "asc"
                                            ? "rotate(180deg)"
                                            : undefined,
                                }}
                            />
                        </span>
                    </div>
                ),
                value: "role",
                className: `${styles.centerKey} ${styles.scoreHeaderClickable}`,
                width: "110px",
                onClick: () => {
                    if (sortBy !== "role") {
                        setSortBy("role");
                        setScoreSortOrder("asc");
                    } else {
                        setScoreSortOrder((prev) =>
                            prev === "desc" ? "asc" : "desc"
                        );
                    }
                    setPage(1);
                },
            },
            { label: "STATUT", value: "status", className: styles.centerKey },
            {
                label: (
                    <div className={styles.scoreHeaderContent}>
                        SCORE
                        <span className={styles.scoreSortIcon}>
                            <ArrowDownIcon
                                width={12}
                                style={{
                                    transform:
                                        sortBy === "score" &&
                                        scoreSortOrder === "asc"
                                            ? "rotate(180deg)"
                                            : undefined,
                                }}
                            />
                        </span>
                    </div>
                ),
                value: "score",
                className: `${styles.centerKey} ${styles.scoreHeaderClickable}`,
                onClick: () => {
                    if (sortBy !== "score") {
                        setSortBy("score");
                        setScoreSortOrder("desc");
                    } else {
                        setScoreSortOrder((prev) =>
                            prev === "desc" ? "asc" : "desc"
                        );
                    }
                    setPage(1);
                },
            },
            {
                label: "BLOCAGE PRINCIPALE",
                value: "mainBlocker",
                className: styles.centerKey,
            },
            {
                label: "ACTION",
                value: "validationAction",
                className: styles.centerKey,
                width: "220px",
            },
            {
                label: "DATE PASSAGE INDÉPENDANT",
                value: "validatedAt",
                className: styles.centerKey,
                width: "160px",
            },
        ],
        [scoreSortOrder, sortBy]
    );

    const data = useMemo(
        () =>
            users.map((user) => {
                const nameFromAdminPattern = getUserName(
                    user.firstname ?? "",
                    user.lastname ?? "",
                    user.pseudo ?? "",
                    isStreamOn
                )
                    .replace(/\s+/g, " ")
                    .trim();
                const displayName =
                    nameFromAdminPattern || user.email || "Utilisateur";
                const score = Number(user.eligibility?.score ?? 0);
                const scoreClamped = Math.min(100, Math.max(0, score));
                const scoreText = `${score.toFixed(2).replace(/\.?0+$/, "")}%`;
                const isTrained = user.status === "trained";
                const isEligible = user.status === "eligible";
                const statusClassName = isTrained
                    ? styles.statusCertified
                    : isEligible
                    ? styles.statusEligible
                    : styles.statusInProgress;
                const scoreValueClassName = isTrained
                    ? styles.scoreValueCertified
                    : isEligible
                    ? styles.scoreValueEligible
                    : styles.scoreValueInProgress;
                const scoreFillClassName =
                    isTrained
                        ? styles.scoreFillCertified
                        : scoreClamped === 100
                        ? styles.scoreFillGreen
                        : scoreClamped >= 50
                        ? styles.scoreFillOrange
                        : styles.scoreFillRed;
                const blocker = user.mainBlocker;
                const blockerClassName =
                    blocker === "sessions"
                        ? styles.blockerOrange
                        : blocker === "day_win" || blocker === "funding"
                        ? styles.blockerRed
                        : styles.blockerNeutral;
                const validatedAtRaw = user.validatedBy?.validatedAt;
                const validatedAtLabel = validatedAtRaw
                    ? getFormattedFrDate(new Date(validatedAtRaw))
                    : null;
                const nextRoleName = getNextTraderRole(user.role);
                const previousRoleName = getPreviousTraderRole(user.role);
                const showPromote = canShowPromoteAction(
                    nextRoleName,
                    scoreClamped
                );
                const showDemote = canShowDemoteAction(previousRoleName);
                const canShowValidationAction = showPromote || showDemote;
                const promoteLabel = getPromoteButtonLabel(nextRoleName);
                const demoteLabel = getDemoteButtonLabel(
                    previousRoleName,
                    user.role
                );
                const isActionLoading = actionLoadingUserId === user.userId;
                const handleValidate = async () => {
                    setActionLoadingUserId(user.userId);
                    try {
                        await validateAdminGraduation(user.userId);
                        toast.success(
                            nextRoleName === Role.Forme
                                ? `Membre validé ${Role.Forme}.`
                                : `Promotion vers ${nextRoleName} effectuée.`
                        );
                        setRefreshKey((prev) => prev + 1);
                    } catch {
                        toast.error("Échec de la promotion.");
                    } finally {
                        setActionLoadingUserId(null);
                    }
                };
                return {
                    id: user.userId,
                    onClick: () => {
                        const currentUrl =
                            window.location.pathname + window.location.search;
                        sessionStorage.setItem("returnToSearchUrl", currentUrl);
                        navigate(
                            `/formation?userId=${encodeURIComponent(user.userId)}`
                        );
                    },
                    member: (
                        <div className={styles.userPseudo}>
                            <span
                                className={styles.notesIcon}
                                onClick={(event) => event.stopPropagation()}
                            >
                                <IconButton
                                    size="medium"
                                    icon={InfoIcon}
                                    onClick={() =>
                                        setNotesUser({
                                            id: user.userId,
                                            firstname: user.firstname ?? "",
                                            lastname: user.lastname ?? "",
                                            pseudo: user.pseudo ?? "",
                                            email: user.email ?? "",
                                        })
                                    }
                                />
                            </span>
                            <div>{displayName}</div>
                            <OpenInNewTabLink
                                href={`/formation?userId=${encodeURIComponent(user.userId)}`}
                            />
                        </div>
                    ),
                    role: (
                        <span className={styles.roleBadge}>
                            {user.role ?? "-"}
                        </span>
                    ),
                    status: (
                        <span
                            className={`${styles.statusBadge} ${statusClassName}`}
                        >
                            {GraduationStatusLabel[user.status]}
                        </span>
                    ),
                    score: (
                        <div>
                            <div className={styles.scoreTop}>
                                <span
                                    className={`${styles.scoreValue} ${scoreValueClassName}`}
                                >
                                    {scoreText}
                                </span>
                            </div>
                            <div className={styles.scoreTrack}>
                                <div
                                    className={`${styles.scoreFill} ${scoreFillClassName}`}
                                    style={{ width: `${scoreClamped}%` }}
                                />
                            </div>
                        </div>
                    ),
                    mainBlocker: (
                        <span
                            className={`${styles.blockerBadge} ${blockerClassName}`}
                        >
                            {blockerLabelMap[blocker]}
                        </span>
                    ),
                    validationAction:
                        canValidateAdminGraduation && canShowValidationAction ? (
                        <div className={styles.validationActions}>
                            {showDemote && (
                                <Button
                                    size="newSmall"
                                    variant="secondary"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setCancelValidationUserId(user.userId);
                                    }}
                                    isLoading={isActionLoading}
                                    disabled={isActionLoading}
                                >
                                    {demoteLabel}
                                </Button>
                            )}
                            {showPromote && (
                                <Button
                                    size="newSmall"
                                    variant="primary"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        void handleValidate();
                                    }}
                                    isLoading={isActionLoading}
                                    disabled={isActionLoading}
                                >
                                    {promoteLabel}
                                </Button>
                            )}
                        </div>
                    ) : (
                        <span className={styles.actionNone}>-</span>
                    ),
                    validatedAt: validatedAtLabel ? (
                        <span className={styles.validatedAt}>{validatedAtLabel}</span>
                    ) : (
                        <span className={styles.actionNone}>-</span>
                    ),
                };
            }),
        [actionLoadingUserId, canValidateAdminGraduation, isStreamOn, navigate, users]
    );

    return (
        <ConnectedLayout>
            <ConfirmModal
                open={Boolean(cancelValidationUserId)}
                handleClose={() => setCancelValidationUserId(null)}
                handleConfirm={() => {
                    void handleConfirmCancelValidation();
                }}
                isLoading={Boolean(actionLoadingUserId)}
                description={(() => {
                    const target = users.find(
                        (user) => user.userId === cancelValidationUserId
                    );
                    if (target?.role === Role.Forme) {
                        return `Cette action annulera la validation ${Role.Forme} et rétrogradera le membre vers ${Role.Independant}.`;
                    }
                    const previousRoleName = getPreviousTraderRole(target?.role);
                    return `Cette action rétrogradera le membre vers ${
                        previousRoleName ?? "le niveau précédent"
                    }.`;
                })()}
            />
            <ConfirmModal
                open={bulkPromoteConfirmOpen}
                handleClose={() => {
                    if (!bulkPromoteLoading) setBulkPromoteConfirmOpen(false);
                }}
                handleConfirm={() => {
                    void handleConfirmBulkPromoteEligible();
                }}
                isLoading={bulkPromoteLoading}
                title={`Promouvoir à partir de ${GRADUATION_VALIDATION_MIN_PROGRESS} % ?`}
                confirmLabel="Promouvoir"
                confirmVariant="primary"
                description={`Vous allez promouvoir tous les membres de « ${selectedPromotionName} » dont le score global est d'au moins ${GRADUATION_VALIDATION_MIN_PROGRESS} %, comme le bouton de promotion individuelle. Les autres filtres et les autres promotions ne sont pas concernés.`}
            />
            <Header
                icon={GraduationCapIcon}
                title="Apprentissage"
                optionalButton={
                    !isMobile ? (
                        <div className={styles.headerActions}>
                            {canBulkPromoteEligible && (
                                <Button
                                    label={`Promouvoir toutes les personnes à ${GRADUATION_VALIDATION_MIN_PROGRESS} %`}
                                    labelOptions={{ noWrap: true }}
                                    onClick={() => setBulkPromoteConfirmOpen(true)}
                                    size="small"
                                    variant="primary"
                                    disabled={bulkPromoteLoading || loading}
                                />
                            )}
                            {hasPermission("admin-graduation-extract", "read") && (
                                <Button
                                    Icon={DownloadIcon}
                                    label="Exporter"
                                    labelOptions={{ noWrap: true }}
                                    onClick={handleExportGraduationCSV}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                        </div>
                    ) : null
                }
            />
            <div className={styles.container}>
                <section className={styles.titleBlock}>
                    <h2 className={styles.title}>Suivi apprentissage des membres</h2>
                    <p className={styles.subtitle}>
                        Pilotage des critères de progression et de l&apos;éligibilité.
                    </p>
                </section>

                <section className={styles.summaryGrid}>
                    <article className={styles.summaryCard}>
                        <p className={styles.summaryLabel}>
                            MEMBRES ACTIFS (90J)
                        </p>
                        <p className={styles.summaryValue}>{summary.total}</p>
                    </article>
                    <article className={styles.summaryCard}>
                        <p className={styles.summaryLabel}>Éligibles promotion</p>
                        <p className={styles.summaryValue}>{summary.eligible}</p>
                    </article>
                    <article className={styles.summaryCard}>
                        <p className={styles.summaryLabel}>Traders Formés</p>
                        <p className={styles.summaryValue}>{summary.trained}</p>
                    </article>
                    <article className={styles.summaryCard}>
                        <p className={styles.summaryLabel}>En progression</p>
                        <p className={styles.summaryValue}>{summary.in_progress}</p>
                    </article>
                </section>

                <section className={styles.filtersCard}>
                    <div className={styles.filtersRow}>
                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>Recherche</span>
                            <Input
                                type="text"
                                value={searchInput}
                                onChanged={(value) => {
                                    setSearchInput(String(value ?? ""));
                                    setPage(1);
                                }}
                                placeholder="Pseudo, prénom ou nom"
                                className={styles.filterSelect}
                            />
                        </div>
                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>Statut</span>
                            <Select
                                value={status}
                                onChanged={(value) => {
                                    setStatus((value as GraduationUserStatus) || "");
                                    setPage(1);
                                }}
                                className={styles.filterSelect}
                                mobileAriaLabel="Filtre statut"
                            >
                                <option value="">Tous</option>
                                <option value="trained">
                                    {GraduationStatusLabel.trained}
                                </option>
                                <option value="eligible">
                                    {GraduationStatusLabel.eligible}
                                </option>
                                <option value="in_progress">
                                    {GraduationStatusLabel.in_progress}
                                </option>
                            </Select>
                        </div>

                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>Rôle</span>
                            <Select
                                value={roleFilter}
                                onChanged={(value) => {
                                    setRoleFilter(String(value ?? ""));
                                    setPage(1);
                                }}
                                className={styles.filterSelect}
                                mobileAriaLabel="Filtre rôle"
                            >
                                <option value="">Tous</option>
                                {ROLE_FILTER_OPTIONS.map((roleName) => (
                                    <option key={roleName} value={roleName}>
                                        {roleName}
                                    </option>
                                ))}
                            </Select>
                        </div>

                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>Promo</span>
                            <Select
                                value={promotionFilter}
                                onChanged={(value) => {
                                    setPromotionFilter(String(value ?? ""));
                                    setPage(1);
                                }}
                                className={styles.filterSelect}
                                mobileAriaLabel="Filtre promo"
                            >
                                <option value="">Toutes les promos</option>
                                <option value="none">Sans promo</option>
                                {promoOptions.map((promo) => (
                                    <option key={promo.id} value={promo.id}>
                                        {promo.name}
                                    </option>
                                ))}
                            </Select>
                        </div>

                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>
                                Blocage principal
                            </span>
                            <Select
                                value={mainBlocker}
                                onChanged={(value) => {
                                    setMainBlocker(
                                        (value as GraduationMainBlockerFilter) ||
                                            ""
                                    );
                                    setPage(1);
                                }}
                                className={styles.filterSelect}
                                mobileAriaLabel="Filtre blocage principal"
                            >
                                <option value="">Tous</option>
                                <option value="none">Aucun</option>
                                <option value="sessions">Sessions</option>
                                <option value="day_win">Day win</option>
                                <option value="funding">Financement</option>
                            </Select>
                        </div>

                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>Fourchette score</span>
                            <div className={styles.scoreRangeInputs}>
                                <Input
                                    type="number"
                                    value={minScoreInput}
                                    onChanged={(value) => {
                                        const next = String(value ?? "");
                                        if (next === "") {
                                            setMinScoreInput("");
                                            setPage(1);
                                            return;
                                        }
                                        const n = Number(next);
                                        if (isNaN(n)) return;
                                        setMinScoreInput(
                                            String(Math.min(100, Math.max(0, n)))
                                        );
                                        setPage(1);
                                    }}
                                    min="0"
                                    max="100"
                                    placeholder="Min"
                                    className={styles.scoreInput}
                                />
                                <span className={styles.scoreRangeSeparator}>-</span>
                                <Input
                                    type="number"
                                    value={maxScoreInput}
                                    onChanged={(value) => {
                                        const next = String(value ?? "");
                                        if (next === "") {
                                            setMaxScoreInput("");
                                            setPage(1);
                                            return;
                                        }
                                        const n = Number(next);
                                        if (isNaN(n)) return;
                                        setMaxScoreInput(
                                            String(Math.min(100, Math.max(0, n)))
                                        );
                                        setPage(1);
                                    }}
                                    min="0"
                                    max="100"
                                    placeholder="Max"
                                    className={styles.scoreInput}
                                />
                            </div>
                        </div>
                        {hasActiveFilters && (
                            <div className={styles.filterResetField}>
                                <Button
                                    size="small"
                                    variant="secondary"
                                    onClick={handleResetFilters}
                                    label="Réinitialiser les filtres"
                                />
                            </div>
                        )}
                    </div>
                </section>

                <section className={styles.tableCard}>
                    <Table
                        rows={rows}
                        data={data}
                        className={styles.table}
                        loading={loading}
                        showHeaderWhenLoading
                        noDataLabel="Aucun membre trouvé avec ces filtres."
                    />
                </section>

                <div className={styles.paginationRow}>
                    <Pagination
                        count={maxPage}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                    />
                    <Select
                        value={String(limit)}
                        onChanged={(value) => {
                            setLimit(Number(value));
                            setPage(1);
                        }}
                        className={styles.paginationSelect}
                        mobileAriaLabel="Nombre de lignes par page"
                    >
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </Select>
                </div>
            </div>
            {notesUser && (
                <MemberDetails
                    open
                    handleClose={() => setNotesUser(null)}
                    user={notesUser}
                />
            )}
        </ConnectedLayout>
    );
};

export default GraduationAdmin;
